// El token de Airtable ya NO vive en el frontend: las peticiones pasan por el
// proxy del backend (/api/airtable), que añade la autorización del lado servidor.
// Se mantiene AIRTABLE_HEADERS como objeto vacío para no romper los spreads
// existentes; las cookies de sesión se envían automáticamente (mismo origen).
export const AIRTABLE_HEADERS = {};

export const TABLES = {
  employees: process.env.REACT_APP_AIRTABLE_EMPLOYEES_TABLE_ID || 'tblT0jxHfbBkwQ3sp',
  bosses: process.env.REACT_APP_AIRTABLE_BOSSES_TABLE_ID || 'tbldfyzObJwdmss8P',
  requests: process.env.REACT_APP_AIRTABLE_REQUESTS_TABLE_ID || 'tbl89f0pfcGR0syQK',
};

export const airtableUrl = (table) => `/api/airtable/${table}`;

let loadingCounter = 0;
const loadingListeners = new Set();

const notifyLoading = () => {
  loadingListeners.forEach((fn) => fn(loadingCounter > 0));
};

export const subscribeLoading = (fn) => {
  loadingListeners.add(fn);
  fn(loadingCounter > 0);
  return () => loadingListeners.delete(fn);
};

const startLoading = () => {
  loadingCounter++;
  notifyLoading();
};

const endLoading = () => {
  loadingCounter = Math.max(0, loadingCounter - 1);
  notifyLoading();
};

// --- Manejo de sesión expirada / re-login automático ------------------------
// Cuando el backend responde 401/403 (sesión caducada, p. ej. tras reiniciar el
// servicio), se dispara este handler para llevar al usuario al login sin recarga.
let sessionExpiredHandler = null;
let sessionExpiredFired = false;

export const setSessionExpiredHandler = (fn) => {
  sessionExpiredHandler = fn;
};

// Se llama tras un login exitoso para volver a armar la detección.
export const resetSessionExpired = () => {
  sessionExpiredFired = false;
};

const triggerSessionExpired = () => {
  if (sessionExpiredFired) return; // evita disparos múltiples por llamadas concurrentes
  sessionExpiredFired = true;
  if (sessionExpiredHandler) {
    sessionExpiredHandler();
  } else {
    window.location.href = '/login';
  }
};

export const fetchWithRetry = async (url, options = {}, retries = 2) => {
  startLoading();
  try {
    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, options);
        // Sesión caducada: redirigir al login (no se reintenta).
        if (response.status === 401 || response.status === 403) {
          triggerSessionExpired();
          return response;
        }
        const retryable = response.status === 429 || (response.status >= 500 && response.status < 600);
        if (retryable && attempt < retries) {
          await new Promise((r) => setTimeout(r, Math.pow(2, attempt) * 500));
          continue;
        }
        return response;
      } catch (err) {
        lastError = err;
        if (attempt >= retries) throw err;
        await new Promise((r) => setTimeout(r, Math.pow(2, attempt) * 500));
      }
    }
    throw lastError || new Error('Fetch falló tras múltiples intentos');
  } finally {
    endLoading();
  }
};

// `params` admite query params de Airtable (p. ej. { filterByFormula }). La
// paginación con `offset` se conserva intacta y se combina con esos params.
export const fetchAllAirtableData = async (url, params = {}, allRecords = [], offset = null) => {
  try {
    const search = new URLSearchParams(params);
    if (offset) search.set('offset', offset);
    const qs = search.toString();
    const apiUrl = qs ? `${url}?${qs}` : url;

    const response = await fetchWithRetry(apiUrl, {
      headers: AIRTABLE_HEADERS,
      // Envía la cookie de sesión al proxy del backend (mismo origen).
      credentials: 'same-origin',
      // Evita que el navegador sirva datos cacheados al refrescar la interfaz.
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Error al conectar con Airtable');
    }

    const data = await response.json();
    const records = [...allRecords, ...data.records];

    if (data.offset) {
      return await fetchAllAirtableData(url, params, records, data.offset);
    }

    return records;
  } catch (error) {
    console.error('Error al obtener los datos de Airtable:', error);
    throw error;
  }
};

const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

export const fetchCached = async (key, fetcher) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.time < CACHE_TTL) {
    return cached.data;
  }
  const data = await fetcher();
  cache.set(key, { time: Date.now(), data });
  return data;
};

export const invalidateCache = (key) => {
  if (key) cache.delete(key);
  else cache.clear();
};

let toastSeq = 0;
const toastListeners = new Set();
let currentToasts = [];

const notifyToasts = () => {
  toastListeners.forEach((fn) => fn(currentToasts));
};

export const subscribeToasts = (fn) => {
  toastListeners.add(fn);
  fn(currentToasts);
  return () => toastListeners.delete(fn);
};

export const showToast = (variant, title, message, duration = 4000) => {
  const id = ++toastSeq;
  currentToasts = [...currentToasts, { id, variant, title, message }];
  notifyToasts();
  if (duration > 0) {
    setTimeout(() => {
      currentToasts = currentToasts.filter((t) => t.id !== id);
      notifyToasts();
    }, duration);
  }
  return id;
};

export const dismissToast = (id) => {
  currentToasts = currentToasts.filter((t) => t.id !== id);
  notifyToasts();
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  let date;
  if (dateString.includes('T')) {
    const [year, month, day] = dateString.split('T')[0].split('-');
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateString + 'T00:00:00');
  }

  if (isNaN(date.getTime())) return 'N/A';

  const dayFormatted = String(date.getDate()).padStart(2, '0');
  const monthFormatted = String(date.getMonth() + 1).padStart(2, '0');
  const yearFormatted = date.getFullYear();
  return `${dayFormatted}/${monthFormatted}/${yearFormatted}`;
};

export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
};

export const validateEmail = (email) => {
  if (!email) return false;
  const normalizedEmail = normalizeEmail(email);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(normalizedEmail) && !normalizedEmail.includes(' ');
};

export const isProblematicEmail = (email) => {
  if (!email) return true;
  const normalized = normalizeEmail(email);
  return (
    email !== normalized ||
    email.includes(' ') ||
    !validateEmail(email) ||
    normalized === 'n/a' ||
    normalized === ''
  );
};

export const parseVigenciaDate = (dateStr, isEndDate = false) => {
  if (!dateStr || dateStr === 'No aplica') {
    return isEndDate ? new Date(2099, 11, 31) : new Date(0);
  }

  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  }

  return new Date(dateStr);
};

export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
};

export const formatDateRange = (startStr, endStr) => {
  const s = formatDate(startStr);
  const e = formatDate(endStr);
  if (s === 'N/A' && e === 'N/A') return 'N/A';
  if (s === e) return s;
  return `${s} – ${e}`;
};

export const calendarDaysBetween = (startStr, endStr) => {
  if (!startStr || !endStr) return 0;
  try {
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    start.setUTCHours(0, 0, 0, 0);
    end.setUTCHours(0, 0, 0, 0);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(0, diff);
  } catch {
    return 0;
  }
};
